import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from "react";

import { message, notification } from "antd";

import AppContext from "../../../context/AppContext";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { useGetGamePassTheme } from "../../../queries/game-pass/useGetGamePassTheme";
import { usePatchGamePassTheme } from "../../../queries/game-pass/usePatchGamePassTheme";
import { uploadGamePassThemeAsset } from "../../../utils/gamePassImageUpload";
import type { GamePassThemePatchRequest } from "../../../types/gamePass";
import { Button } from "../../shared";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import ColorSection from "./ColorSection/ColorSection";
import AssetsSection, {
  type AssetFiles,
} from "./AssetsSection/AssetsSection";
import GamePassPreview from "./GamePassPreview/GamePassPreview";
import {
  applyThemeColorsToSeed,
  buildGamePassColorRows,
  buildThemeColorsPatch,
  resolveGamePassPalette,
  seedColors,
} from "./ColorSection/gamePassColors";
import {
  ASSET_FIELDS,
  type AssetKey,
} from "./AssetsSection/assetsSectionData";
import { useGamePassDefaultAssetUrls } from "./gamePassDefaultAssets";

import styles from "./GamePassStyle.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

/**
 * Game Pass → Configuration → Style.
 *
 * Page shell that composes the three independently-owned theming sections
 * into the Figma two-column layout: the Color and Assets cards stack in the
 * left content column while the live mobile preview sits on the right.
 *
 * The page owns the shared theming state — the editable colours map and the
 * uploaded-asset map — and passes it both to the left-hand editors
 * ({@link ColorSection}, {@link AssetsSection}) and the right-hand
 * {@link GamePassPreview}, so every colour tweak or asset upload is reflected
 * live in the in-app mobile preview.
 *
 * On mount it loads the saved theme (`GET /gamepass/v2/admin/theme`) and
 * overlays it on the TCM defaults: colours seed the editable map, asset URLs
 * take precedence over the default icons. Any key the theme doesn't set keeps
 * its default, so an empty theme (the state everywhere today) renders
 * identically to before.
 *
 * The Save button merge-patches only the *dirty* theme back
 * (`PATCH /gamepass/v2/admin/theme`):
 *   - colours: a changed swatch sends its hex, a swatch reset to the palette
 *     default sends `null` (removes the override), untouched keys are omitted.
 *   - assets: a newly-picked file is presigned + uploaded to S3, then its
 *     `public_url` is sent; a slot reset to the TCM default sends `null`.
 * The response is the merged document, which re-seeds the colour editor +
 * baseline and clears the asset working state (the primed theme cache now
 * carries the saved URLs); `published: false` surfaces a "saved, not yet
 * published" notice, an upload/PATCH failure surfaces a retry message.
 */
const GamePassStyle: FC = () => {
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;
  usePageTitle(AppStrings.GAME_PASS_STYLE_PAGE_TITLE);

  const { data: theme, isSuccess: isThemeLoaded } = useGetGamePassTheme();
  // No inline feature flag: this whole page (and therefore the Save action
  // below) is already gated at the route level in `src/App.js` via
  // `protectedElement(<GamePassStyle />, { requiredPermission:
  // "game_pass.configuration.style", requiredProductFallbackFeature: "gamePass",
  // developmentMode: true })`. An admin can only reach this PATCH if the
  // gamePass flag is on and they hold the permission, so a second flag here
  // would be redundant (see AGENTS.md §11 — route gating is the single gate).
  const patchTheme = usePatchGamePassTheme();

  // Resolve the editable colour rows once from the *live* brand palette the app
  // is themed with (the global `--COLOR-*` variables applied at startup), not the
  // bundled trivelta defaults — so the editor + preview match what the brand's
  // gamepass app actually renders. Seed the colours map from those rows and share
  // both with the editor and the preview.
  const colorRows = useMemo(
    () => buildGamePassColorRows(resolveGamePassPalette()),
    [],
  );
  // The bare palette seed (no theme applied) — the "default" a stop resets to.
  // A save sends `null` for any stop the admin has returned to this value.
  const defaultColors = useMemo(() => seedColors(colorRows), [colorRows]);
  const [colors, setColors] = useState<Record<string, string>>(defaultColors);
  const [matchDefaults, setMatchDefaults] = useState(false);

  // The currently-persisted colours (palette defaults overlaid with the saved
  // theme) — the baseline the editor diffs against to build a merge-patch of
  // only the dirty keys. Reset on initial theme load and after each save.
  const baselineColorsRef = useRef<Record<string, string>>(defaultColors);

  // Overlay the saved theme's colours onto the seeded defaults exactly once,
  // the first time the theme resolves. Guarded so a background refetch never
  // clobbers the admin's in-progress edits.
  const hasSeededFromTheme = useRef(false);
  const themeColors = theme?.colors;
  useEffect(() => {
    if (hasSeededFromTheme.current || !isThemeLoaded) return;
    hasSeededFromTheme.current = true;
    if (themeColors && Object.keys(themeColors).length > 0) {
      const seeded = applyThemeColorsToSeed(seedColors(colorRows), themeColors);
      setColors(seeded);
      baselineColorsRef.current = seeded;
    }
  }, [isThemeLoaded, themeColors, colorRows]);

  // Merge-patch of only the colours that differ from what's persisted. Empty
  // when nothing has changed, which both disables Save and short-circuits it.
  const colorsPatch = useMemo(
    () =>
      buildThemeColorsPatch(
        colorRows,
        colors,
        baselineColorsRef.current,
        defaultColors,
      ),
    [colorRows, colors, defaultColors],
  );
  const hasColorChanges = Object.keys(colorsPatch).length > 0;

  // ── Assets ────────────────────────────────────────────────────────────────
  // Default cash / coins / challenge-complete icons, resolved from the TCM
  // assets-manifest (bundled trivelta copies until the fetch settles). Shown in
  // the Assets tiles and the preview until the admin uploads a replacement.
  const defaultAssetUrls = useGamePassDefaultAssetUrls();

  // Newly-picked files per slot (not yet uploaded), and the slots the admin has
  // reset back to the TCM default (persisted as `null` on save).
  const [assetFiles, setAssetFiles] = useState<AssetFiles>({});
  const [resetAssets, setResetAssets] = useState<ReadonlySet<AssetKey>>(
    () => new Set(),
  );

  // Which known asset slots the saved theme currently overrides.
  const savedThemeAssetKeys = useMemo<ReadonlySet<AssetKey>>(() => {
    const themeAssets = theme?.assets;
    if (!themeAssets) return new Set();
    return new Set(
      ASSET_FIELDS.filter(({ key }) => {
        const url = themeAssets[key];
        return typeof url === "string" && Boolean(url);
      }).map(({ key }) => key),
    );
  }, [theme?.assets]);

  // The image each tile / the preview shows when no file is picked: a saved
  // theme override wins over the TCM default, unless the admin has reset that
  // slot (then it falls back to the TCM default). Only known slots are honoured
  // so an unexpected theme key can't leak into the tiles.
  const effectiveAssetUrls = useMemo<Partial<Record<AssetKey, string>>>(() => {
    const themeAssets = theme?.assets;
    const merged: Partial<Record<AssetKey, string>> = { ...defaultAssetUrls };
    if (themeAssets) {
      ASSET_FIELDS.forEach(({ key }) => {
        if (resetAssets.has(key)) return;
        const url = themeAssets[key];
        if (typeof url === "string" && url) merged[key] = url;
      });
    }
    return merged;
  }, [defaultAssetUrls, theme?.assets, resetAssets]);

  // Slots showing a saved override (no in-session file, not reset) get a delete
  // affordance so the admin can reset them to the TCM default.
  const resettableSlots = useMemo<ReadonlySet<AssetKey>>(
    () =>
      new Set(
        ASSET_FIELDS.filter(
          ({ key }) =>
            savedThemeAssetKeys.has(key) &&
            !resetAssets.has(key) &&
            !assetFiles[key],
        ).map(({ key }) => key),
      ),
    [savedThemeAssetKeys, resetAssets, assetFiles],
  );

  const handleAssetChange = useCallback(
    (key: AssetKey, file: File | null) => {
      if (file) {
        // A fresh upload supersedes any pending reset for the slot.
        setAssetFiles((prev) => ({ ...prev, [key]: file }));
        setResetAssets((prev) => {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        return;
      }

      // Delete: drop any in-session file. If the slot had no in-session file
      // but shows a saved override, mark it for reset-to-default (null on save).
      const hadFile = Boolean(assetFiles[key]);
      setAssetFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (!hadFile && savedThemeAssetKeys.has(key)) {
        setResetAssets((prev) => new Set(prev).add(key));
      }
    },
    [assetFiles, savedThemeAssetKeys],
  );

  const hasAssetChanges = useMemo(
    () =>
      ASSET_FIELDS.some(
        ({ key }) => Boolean(assetFiles[key]) || resetAssets.has(key),
      ),
    [assetFiles, resetAssets],
  );

  // ── Save ──────────────────────────────────────────────────────────────────
  // Uploads run before the PATCH, so track their in-flight state separately and
  // fold it into the single busy flag that locks both editors + the button.
  const [isUploading, setIsUploading] = useState(false);
  const isSaving = isUploading || patchTheme.isPending;

  // Presign + upload each newly-picked file (collecting its `public_url`) and
  // add `null` for every slot reset to default. Throws if any upload fails so
  // the caller can surface the error and skip the PATCH.
  const buildAssetsPatch = useCallback(async (): Promise<
    Record<string, string | null>
  > => {
    const assetsPatch: Record<string, string | null> = {};
    for (const { key } of ASSET_FIELDS) {
      const file = assetFiles[key];
      if (file) {
        // Sequential on purpose: a handful of slots, and it keeps the failure
        // handling simple (first failing upload aborts the whole save).
        // eslint-disable-next-line no-await-in-loop
        assetsPatch[key] = await uploadGamePassThemeAsset({ slot: key, file });
      } else if (resetAssets.has(key)) {
        assetsPatch[key] = null;
      }
    }
    return assetsPatch;
  }, [assetFiles, resetAssets]);

  const handleSave = useCallback(async () => {
    if (!hasColorChanges && !hasAssetChanges) return;

    let assetsPatch: Record<string, string | null>;
    try {
      setIsUploading(true);
      assetsPatch = await buildAssetsPatch();
    } catch {
      message.error(AppStrings.GAME_PASS_STYLE_SAVE_ERROR);
      return;
    } finally {
      setIsUploading(false);
    }

    const body: GamePassThemePatchRequest = {};
    if (hasColorChanges) body.colors = colorsPatch;
    if (Object.keys(assetsPatch).length > 0) body.assets = assetsPatch;
    if (!body.colors && !body.assets) return;

    patchTheme.mutate(body, {
      onSuccess: (saved) => {
        // The response is the merged document. Re-seed the colour editor +
        // baseline so the next diff starts clean...
        const merged = applyThemeColorsToSeed(
          seedColors(colorRows),
          saved.colors ?? {},
        );
        setColors(merged);
        baselineColorsRef.current = merged;
        // ...and clear the asset working state: the primed theme cache now
        // carries the saved URLs, so the tiles render straight from it.
        setAssetFiles({});
        setResetAssets(new Set());

        if (saved.published === false) {
          notification.warning({
            message: AppStrings.GAME_PASS_STYLE_SAVE_PUBLISHED_PENDING,
          });
          return;
        }
        message.success(AppStrings.GAME_PASS_STYLE_SAVE_SUCCESS);
      },
      onError: () => {
        message.error(AppStrings.GAME_PASS_STYLE_SAVE_ERROR);
      },
    });
  }, [
    hasColorChanges,
    hasAssetChanges,
    buildAssetsPatch,
    colorsPatch,
    patchTheme,
    colorRows,
  ]);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.GAME_PASS, GAME_PASS_DROPDOWN_LABELS.STYLE);
  }, [setActiveSidebarTab]);

  return (
    <>
      <div className={styles.page} data-testid="game-pass-style">
        <header className={styles.header}>
          <Button
            intent="primary"
            onClick={handleSave}
            loading={isSaving}
            disabled={(!hasColorChanges && !hasAssetChanges) || isSaving}
            data-testid="game-pass-style-save-button"
          >
            {AppStrings.GAME_PASS_STYLE_SAVE_BUTTON}
          </Button>
        </header>

        <div className={styles.layout}>
          <div className={styles.content}>
            <ColorSection
              rows={colorRows}
              colors={colors}
              onColorsChange={setColors}
              matchDefaults={matchDefaults}
              onMatchDefaultsChange={setMatchDefaults}
              disabled={isSaving}
            />
            <AssetsSection
              files={assetFiles}
              onFileChange={handleAssetChange}
              defaultUrls={effectiveAssetUrls}
              resettableSlots={resettableSlots}
              disabled={isSaving}
            />
          </div>
          <div className={styles.preview}>
            <GamePassPreview
              rows={colorRows}
              colors={colors}
              assetFiles={assetFiles}
              defaultAssetUrls={effectiveAssetUrls}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GamePassStyle;
